// Copyright (c) Docugami, Inc. All rights reserved.

import { setBindingsThunk } from "components/reports/reportsActions";
import { useCallback, useEffect, useState } from "react";
import { setSelectedLabel } from "store/reports/reportsSlice";
import { selectReportEntryBindings, selectReportId, selectReportViewMode, selectSelectedEntry, selectSelectedLabel } from "store/reports/selectors";
import { selectChunkThunk } from "./chunkSelection";
import { useDocumentDgml } from "./documentDgml";
import { useAppDispatch, useAppSelector } from "./redux";
import { useRemoveLabelsMutation } from "store/frontend/frontendSlice";
import { createFolderId } from "components/file_management/file_helpers";
import { createSelectLabelId } from "store/dgml/dgmlSelectors";
import { folderIdSelector } from "store/dgml/dgmlSelectors";

interface ILabelMultiSelect {
    isActive: boolean;
    multiSelectChunks: string[];
    cancelMultiSelect: () => void;
    confirmMultiSelectChunks: (spanIds?: string[]) => void;
}

const selectLabelId = createSelectLabelId();
// Hook to manage multi-select chunks state for a selected docset label
export function useLabelChunkMultiSelect(documentId: string): ILabelMultiSelect {
    const [multiSelectChunks, setMultiSelectChunks] = useState<string[]>([]);

    const { clusterId, fileId } = createFolderId(documentId);
    const selectedLabel = useAppSelector(selectSelectedLabel);
    const selectedLabelId = useAppSelector(state => selectLabelId(state, clusterId, selectedLabel ?? ''));

    // Report-specific state
    const reportId = useAppSelector(selectReportId);
    const folderId = folderIdSelector(documentId);
    const selectedEntryId = useAppSelector(selectSelectedEntry)?.id;
    const viewMode = useAppSelector(selectReportViewMode);
    const documentBindings = useAppSelector(state => selectReportEntryBindings(state, folderId?.fileId ?? ''));

    const { getSpanIdsForLabel, label } = useDocumentDgml();
    const [triggerRemoveLabel] = useRemoveLabelsMutation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        // Reset selected label state when the document or report view mode changes
        dispatch(setSelectedLabel(undefined));
    }, [documentId, viewMode, dispatch])

    // Only set the multi-select chunks from the bindings at the time of the selected label changing.
    // This is to ensure the multi-selection chunks state does not change while actively editing.
    useEffect(() => {
        if (!selectedLabel || !documentId || !documentBindings) {
            return;
        }

        let spanIds: string[] = [];
        // If there is an entry associated with this label, use the entry bindings
        if (!!selectedEntryId) {
            spanIds = documentBindings.entities[selectedEntryId]?.bindings.map(x => x.spanId).filter(x => !!x) as string[] ?? [];
        }
        else {
            spanIds = getSpanIdsForLabel(selectedLabel);
        }

        const dedupedSpanIds = new Set(spanIds);
        setMultiSelectChunks(Array.from(dedupedSpanIds));
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedLabel])

    const completeMultiSelect = useCallback(() => {
        // Reset selected label and selected chunk state
        dispatch(setSelectedLabel(undefined));
        dispatch(selectChunkThunk());
    }, [dispatch]);

    const confirmMultiSelectChunks = useCallback((spanIds?: string[]) => {
        if (!documentId || !selectedLabel) {
            return;
        }

        if (!!selectedEntryId) {
            dispatch(setBindingsThunk(
                reportId,
                {
                    folderId: documentId,
                    entryId: selectedEntryId,
                    spanIds: !!spanIds?.length ? spanIds : undefined
                },
                true
            ));
        }
        else {
            if (!!spanIds?.length) {
                spanIds.forEach(spanId => label({
                    spanId: spanId,
                    label: selectedLabel
                }));
            }

            const removedChunks = multiSelectChunks.filter(x => !spanIds?.includes(x));
            if (!!removedChunks?.length && !!selectedLabelId) {
                triggerRemoveLabel({
                    docsetId: clusterId,
                    fileId: fileId,
                    labelId: selectedLabelId,
                    spanIds: removedChunks
                });
            }
        }

        completeMultiSelect();
    }, [reportId, documentId, clusterId, fileId, selectedLabelId, selectedEntryId, dispatch, completeMultiSelect, selectedLabel, label, multiSelectChunks, triggerRemoveLabel]);

    return {
        isActive: !!selectedLabel,
        multiSelectChunks,
        cancelMultiSelect: completeMultiSelect,
        confirmMultiSelectChunks
    }
}
